import matchMapper from 'modules/client/tournaments/domain/mappers/match.mapper';
import playersMapper from 'modules/client/tournaments/domain/mappers/players.mapper';
import stageMapper from 'modules/client/tournaments/domain/mappers/stage.mapper';
import teamMapper from 'modules/client/tournaments/domain/mappers/team.mapper';
import type { Match, MatchEvents, Player, Stage, Team } from 'modules/client/tournaments/domain/types';
import type {
  ChampionshipApplicationDto,
  ChampionshipDto,
  ChampionshipTableDto,
  MatchEntityDto,
  StageDto,
} from 'modules/client/tournaments/domain/types/dtos';
import graphqlResource, { GraphqlResource } from 'modules/core/graphql-resource';

export class TournamentRepository {
  constructor(private readonly graphql: GraphqlResource) {}

  public async getTeams(championshipId: number): Promise<Team[]> {
    const query = `#graphql
    query getChampionshipTeams($championshipId: Int!) {
      championship(id: $championshipId) {
        id
        championshipTable {
          rows {
            position
            gamesPlayed
            gamesWon
            gamesDraw
            gamesLost
            goalsWon
            goalsLost
            championshipScore
            team {
              id
              name
              emblem {
                externalUrl
              }
            }
            lastGamesResults {
              result
            }
          }
        }
      }
    }
    `;

    const variables = { championshipId };
    const response = await this.graphql.query<{
      data: {
        championship: {
          id: number;
          championshipTable?: ChampionshipTableDto;
        };
      };
    }>(query, variables);

    const { championship } = response.data.data;
    const rows = championship.championshipTable?.rows;

    return rows?.map(teamMapper) ?? [];
  }

  public async getPlayers(championshipId: number): Promise<Player[]> {
    const query = `#graphql
    query getChampionshipPlayers($championshipId: Int!) {
      championship(id: $championshipId) {
        id
        championshipApplications {
          id
          team {
            id
          }
          user {
            id
            name
            surname
            middleName
            isReal
          }
          number
          position
          state
        }
      }
    }
    `;

    const variables = { championshipId };
    const response = await this.graphql.query<{
      data: {
        championship: {
          id: number;
          championshipApplications: ChampionshipApplicationDto[];
        };
      };
    }>(query, variables);

    const { championship } = response.data.data;
    const championshipApplications = championship.championshipApplications;
    return playersMapper(championshipApplications);
  }

  public async getMatches(championshipId: number, teams: Team[]): Promise<Match[]> {
    const query = `#graphql
    query getChampionshipMatches($championshipId: Int!) {
      championship(id: $championshipId) {
        id
        matches {
          id
          teamMatches {
            score
            team {
              id
            }
            teamOrder
          }
          date
          location {
            name
          }
          round {
            name
            id
            stage {
              id
              name
            }
          }
        }
      }
    }
    `;

    const variables = { championshipId };
    const response = await this.graphql.query<{
      data: {
        championship: {
          id: number;
          matches: Pick<MatchEntityDto, 'date' | 'teamMatches' | 'location' | 'id' | 'round'>[];
        };
      };
    }>(query, variables);

    const { championship } = response.data.data;
    const matches = championship.matches;
    return matchMapper(matches, teams);
  }

  public async getStage(championshipId: number, matches: Match[]): Promise<Stage[]> {
    const query = `#graphql
    query getChampionshipStage($championshipId: Int!) {
      championship(id: $championshipId) {
        id
        stages {
          id
          name
          type
          stageChampionshipTable {
            rows {
              team {
                id
              }
              position
              gamesPlayed
              gamesWon
              gamesDraw
              gamesLost
              goalsWon
              goalsLost
              championshipScore
              lastGamesResults {
                result
              }
            }
          }
          teamGroups {
            id
            name
            teams {
              id
            }
            sortOrder
            createdAt
          }
          rounds {
            id
            name
          }
          tournamentBrackets {
            id
            rank
            childBracket {
              id
            }
            parentBrackets {
              id
            }
            round {
              id
              name
            }
            match {
              id
              teamMatches {
                score
                team {
                  id
                }
                teamOrder
              }
            }
          }
        }
      }
    }
    `;

    const variables = { championshipId };
    const response = await this.graphql.query<{
      data: {
        championship: {
          id: number;
          stages: StageDto[];
        };
      };
    }>(query, variables);

    const { championship } = response.data.data;
    const { stages } = championship;
    return stages.map((stage) => stageMapper(stage, matches));
  }

  public async getChampionshipInfo(championshipId: number): Promise<ChampionshipDto> {
    const query = `#graphql
    query getChampionshipInfo($championshipId: Int!) {
      championship(id: $championshipId) {
        id
        name
        tournament {
          id
          name
        }
        startDate
        endDate
      }
    }
    `;

    const variables = { championshipId };
    const response = await this.graphql.query<{
      data: {
        championship: ChampionshipDto;
      };
    }>(query, variables);

    const { championship } = response.data.data;
    return championship;
  }

  public async generateStageMatches(stageId: number): Promise<Stage> {
    const mutation = `#graphql
    mutation generateStageMatches($stageId: Int!) {
      generateStageMatches(stageId: $stageId) {
        id
        name
        type
        stageChampionshipTable {
          rows {
            team {
              id
            }
            position
            gamesPlayed
            gamesWon
            gamesDraw
            gamesLost
            goalsWon
            goalsLost
            championshipScore
            lastGamesResults {
              result
            }
          }
        }
        teamGroups {
          id
          name
          teams {
            id
          }
          sortOrder
          createdAt
        }
        rounds {
          id
          name
        }
        tournamentBrackets {
          id
          rank
          childBracket {
            id
          }
          parentBrackets {
            id
          }
          round {
            id
            name
          }
          match {
            id
            teamMatches {
              score
              team {
                id
              }
              teamOrder
            }
          }
        }
      }
    }
    `;

    const variables = { stageId };
    const response = await this.graphql.query<{
      data: {
        generateStageMatches: StageDto;
      };
    }>(mutation, variables);

    const stage = response.data.data.generateStageMatches;
    return stageMapper(stage, []);
  }

  public async getMatchEvents(championshipId: number): Promise<MatchEvents[]> {
    const query = `#graphql
    query getChampionshipMatchEvents($championshipId: Int!) {
      championship(id: $championshipId) {
        id
        matches {
          id
          matchEvents {
            id
            type
            user {
              id
            }
          }
        }
      }
    }
    `;

    const variables = { championshipId };
    const response = await this.graphql.query<{
      data: {
        championship: {
          id: number;
          matches: Pick<MatchEntityDto, 'id' | 'matchEvents'>[];
        };
      };
    }>(query, variables);

    const { championship } = response.data.data;
    return championship.matches
      .map(
        (match) =>
          match.matchEvents?.map((matchEvent) => {
            return {
              id: matchEvent.id,
              matchId: match.id,
              userId: matchEvent.user?.id,
              secondaryUserId: matchEvent.user?.id,
              type: matchEvent.type,
            };
          }) ?? [],
      )
      .flat();
  }
}

const tournamentRepository = new TournamentRepository(graphqlResource);

export default tournamentRepository;
